import axios from 'axios';

// Create public axios instance for non-authenticated endpoints
const apiPublic = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
});

// Request interceptor for public endpoints
apiPublic.interceptors.request.use(
  (config) => {
    // Remove Content-Type header for FormData to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`[Public API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('[Public API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor for public endpoints
apiPublic.interceptors.response.use(
  (response) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`[Public API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }

    return response;
  },
  (error) => {
    // Handle different types of errors securely
    if (error.response) {
      const { status, data } = error.response;
      
      // Log error details in development only
      if (import.meta.env.DEV) {
        console.error(`[Public API Error] ${status}`, data);
      }

      // Handle rate limiting
      if (status === 429) {
        return Promise.reject({
          message: data.message || 'Too many requests. Please try again later.',
          status: 429,
          retryAfter: data.retryAfter
        });
      }

      // Handle validation errors
      if (status === 400) {
        return Promise.reject({
          message: data.message || 'Invalid request data',
          status: 400,
          errors: data.errors || []
        });
      }

      // Handle server errors
      if (status >= 500) {
        return Promise.reject({
          message: 'Server error. Please try again later.',
          status: status,
          requestId: data.requestId
        });
      }

      // Generic error response
      return Promise.reject({
        message: data.message || 'An error occurred',
        status: status,
        data: data
      });
    }

    // Network or other errors
    if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        status: 0,
        networkError: true
      });
    }

    // Timeout errors
    if (error.code === 'ECONNABORTED') {
      return Promise.reject({
        message: 'Request timeout. Please try again.',
        status: 408,
        timeout: true
      });
    }

    // Generic error
    console.error('[Public API Error]', error);
    return Promise.reject({
      message: 'An unexpected error occurred',
      status: 0,
      originalError: error
    });
  }
);

// Public API methods
export const publicAPI = {
  // Get locations (public endpoint)
  getLocations: () => {
    return apiPublic.get('/public/locations');
  },

  // Health check (public endpoint)
  healthCheck: () => {
    return apiPublic.get('/health');
  },

  // Track ticket by tracking ID (public endpoint - no authentication required)
  getTicket: (trackingId) => {
    return apiPublic.get(`/tickets/track/${trackingId}`);
  },

  // Create troubleshooting ticket (public endpoint)
  createTroubleshootingTicket: (ticketData) => {
    return apiPublic.post('/tickets/troubleshooting', ticketData);
  }
};

// Export the public axios instance
export default apiPublic;

// Export individual methods for convenience
export const { getLocations, healthCheck, getTicket, createTroubleshootingTicket } = publicAPI;
